import axios, { type AxiosResponse } from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { logger } from '@/utils/logger';
import { getAPIErrorMessage } from '@/types/api';
import router from '@/router';

export async function getAccessToken(): Promise<string | null> {
  const authStore = useAuthStore();
  return authStore.token;
}

const api = axios.create({
  withCredentials: true,
});

// Request interceptor: attach a Trace-Id header to every outgoing request
api.interceptors.request.use((config) => {
  config.headers.set('Trace-Id', crypto.randomUUID());
  return config;
});

// Response interceptor: redirect to login on auth failure if user was authenticated
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const code = error.response?.data?.code;
    const isAuthError = status === 401 || code === 'token_expired' || code === 'token_blacklisted';

    if (isAuthError) {
      const authStore = useAuthStore();
      if (authStore.checkSession()) {
        logger.debug('Session expired, redirecting to login', code);
        authStore.stopHeartbeat();
        authStore.logOff();
        router.push({ path: '/login', query: { reason: 'session_expired' } });
      }
    }

    return Promise.reject(error);
  }
);

export default api;

interface UserPermissions {
  name: string;
  fiNumber: string;
  allowedFis: Array<{ fiNumber: string; fiName: string }>;
  privileges: string;
  email: string;
  loginType: string;
}

interface DownloadResponse {
  downloadLink: string;
  fileName: string;
}

interface GeneralResponse {
  message: string;
  status: number;
}

interface FiListItem {
  fiNumber: string;
  fiName: string;
  originalFiNumber: string;
}

interface ShareableUrlResponse {
  shortUrl: string;
  shortKey: string;
  expiresAt: string;
  downloadType: string;
}

interface ShareableUrlSummary {
  shortKey: string;
  shortUrl: string;
  downloadType: string;
  createdAt: string;
  expiresAt: string;
  accessCount: number;
  lastAccessed: string;
}

interface ListShareableUrlsResponse {
  shares: ShareableUrlSummary[];
}

interface HeartbeatResponse {
  status: string;
  message: string;
  loginType?: string;
}

export async function getPermissions(): Promise<UserPermissions> {
  try {
    logger.debug('Fetching permissions');
    const response: AxiosResponse<UserPermissions> = await api.get('/v1/user');
    return response.data;
  } catch (error) {
    logger.error('Get permissions failed:', getAPIErrorMessage(error));
    throw error;
  }
}

export async function fetchFiData(fiNumber: string) {
  logger.debug('Fetching FI data for:', fiNumber);
  const response = await api.get(`/v1/fi-configs/${fiNumber}`);
  return response.data;
}

export async function downloadAppFile(buildId: string): Promise<void> {
  try {
    const response: AxiosResponse<DownloadResponse> = await api.get(`/v1/build/${buildId}/download?type=app`);
    const { downloadLink, fileName } = response.data;
    logger.debug('Downloading file:', fileName);

    const a = document.createElement('a');
    a.href = downloadLink;
    a.download = fileName || 'download';
    a.click();
  } catch (error) {
    logger.error('Download failed:', getAPIErrorMessage(error));
    throw error;
  }
}

export async function downloadAppBundleFile(buildId: string): Promise<void> {
  try {
    const response: AxiosResponse<DownloadResponse> = await api.get(`/v1/build/${buildId}/download?type=aab`);
    const { downloadLink, fileName } = response.data;
    logger.debug('Downloading App Bundle:', fileName);

    const a = document.createElement('a');
    a.href = downloadLink;
    a.download = fileName || 'download';
    a.click();
  } catch (error) {
    logger.error('Download failed:', getAPIErrorMessage(error));
    throw error;
  }
}

export async function downloadBuildArchiveFile(buildId: string): Promise<void> {
  try {
    const response: AxiosResponse<DownloadResponse> = await api.get(`/v1/build/${buildId}/download?type=archive`);
    const { downloadLink, fileName } = response.data;
    logger.debug('Downloading Archive:', fileName);

    const a = document.createElement('a');
    a.href = downloadLink;
    a.download = fileName || 'download';
    a.click();
  } catch (error) {
    logger.error('Download failed:', getAPIErrorMessage(error));
    throw error;
  }
}

export async function deleteBuildById(buildId: string): Promise<GeneralResponse> {
  try {
    const res: AxiosResponse<GeneralResponse> = await api.delete(`/v1/build/${buildId}/mark-for-deletion`);
    return res.data;
  } catch (error) {
    logger.error('Failed to delete build:', getAPIErrorMessage(error));
    throw error;
  }
}

export async function deleteAllBuildsByConfig(configId: string): Promise<GeneralResponse> {
  try {
    logger.debug('Deleting all builds for configId:', configId);
    const response: AxiosResponse<GeneralResponse> = await api.delete(`/v1/configuration/${configId}/mark-all-for-deletion`);
    return response.data;
  } catch (error) {
    logger.error('Failed to delete all builds for configId:', getAPIErrorMessage(error));
    throw error;
  }
}

export async function unmarkBuildById(buildId: string): Promise<GeneralResponse> {
  try {
    const response: AxiosResponse<GeneralResponse> = await api.post(`/v1/build/${buildId}/unmark-for-deletion`);
    return response.data;
  } catch (error) {
    logger.error('Failed to unmark build for deletion:', getAPIErrorMessage(error));
    throw error;
  }
}

export async function fetchUserActions(fiNumber: string) {
  try {
    const res = await api.get(`/v1/${fiNumber}/actions`);
    return res.data;
  } catch (error) {
    logger.error('Failed to fetch user actions:', getAPIErrorMessage(error));
    throw error;
  }
}

export async function heartbeat(): Promise<HeartbeatResponse> {
  const response: AxiosResponse<HeartbeatResponse> = await api.get('/v1/heartbeat');
  return response.data;
}

export async function logoutUser(): Promise<AxiosResponse> {
  return await api.post('/v1/logout');
}

export async function fetchCachedFiList(): Promise<FiListItem[]> {
  try {
    const response: AxiosResponse<FiListItem[]> = await api.get('/v1/cachedfilist');
    const fiList = response.data;

    fiList.sort((a, b) => {
      const numA = parseInt(a.fiNumber, 10);
      const numB = parseInt(b.fiNumber, 10);
      return numA - numB;
    });

    return fiList;
  } catch (error) {
    logger.error('Error fetching all FIs:', getAPIErrorMessage(error));
    throw error;
  }
}

export async function generatePublicDownloadLink(buildId: string, downloadType = 'app'): Promise<ShareableUrlResponse> {
  try {
    const response: AxiosResponse<ShareableUrlResponse> = await api.post(`/v1/build/${buildId}/share?type=${downloadType}`);
    return response.data;
  } catch (error) {
    logger.error('Error generating public download link:', getAPIErrorMessage(error));
    throw error;
  }
}

export function getSideloadManifestUrl(buildId: string): string {
  return `/v1/build/${buildId}/download?type=sideload`;
}

export async function listShareableUrls(buildId: string): Promise<ListShareableUrlsResponse> {
  try {
    const response: AxiosResponse<ListShareableUrlsResponse> = await api.get(`/v1/build/${buildId}/shares`);
    return response.data;
  } catch (error) {
    logger.error('Error listing shareable URLs:', getAPIErrorMessage(error));
    throw error;
  }
}

export async function revokeShareableUrl(fiNumber: string, shortKey: string): Promise<void> {
  try {
    await api.delete(`/v1/s/${fiNumber}/${shortKey}`);
  } catch (error) {
    logger.error('Error revoking shareable URL:', getAPIErrorMessage(error));
    throw error;
  }
}
